import { useState } from "react";
import { Link } from "wouter";
import { SEOHead, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import { SeoPageFooter } from "@/components/seo-page-footer";
import { ChevronRight, ArrowRight, CheckCircle2, ChevronDown } from "lucide-react";

const PAGE_URL = "https://thepropertool.com/guides/quoting-for-uk-builders/";

const WEBPAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Quoting for UK Builders: The Complete Guide (2026)",
  "description": "Everything UK builders need to know about quoting jobs accurately: what quoting software does, how to price construction jobs, UK cost benchmarks, AI vs manual quoting, and the most common mistakes.",
  "url": PAGE_URL,
  "publisher": {
    "@type": "Organization",
    "name": "The Proper Tool",
    "url": "https://thepropertool.com",
  },
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Guides", "item": "https://thepropertool.com/guides/" },
    { "@type": "ListItem", "position": 3, "name": "Quoting for UK Builders", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How do UK builders price a job accurately?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Accurate pricing for UK builders involves five cost components: labour (calculated at your true day rate, including NI, insurance, and overheads); materials (priced from current supplier quotes with a 10–15% wastage uplift); plant and hired equipment (skips, scaffolding, specialist kit); subcontractors (fixed quotes plus a 10–15% management margin); and overhead and profit (typically 10–20% net margin on top of all direct costs). The most common mistake is underestimating labour days and missing scope items entirely.",
      },
    },
    {
      "@type": "Question",
      "name": "What does quoting software do for a UK builder?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Quoting software for UK builders generates a fully itemised scope of works from drawings, photos, or a plain-text description, applies UK market rates for labour and materials, checks for missing items and pricing outliers, and exports a professional PDF quote ready to send to clients. The best tools use a UK-specific cost library and can read structural drawings to extract beam sizes, floor areas, and connection details automatically.",
      },
    },
    {
      "@type": "Question",
      "name": "What are typical UK construction cost benchmarks for 2026?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Indicative UK benchmarks for 2026: single-storey rear extension £1,800–£2,500/m²; double-storey extension £1,600–£2,200/m²; loft conversion £35,000–£65,000 depending on type; garage conversion £15,000–£30,000; kitchen renovation £10,000–£35,000. These are guide figures only: actual costs vary by region, specification, and site conditions. London and the South East typically run 15–25% above national averages.",
      },
    },
    {
      "@type": "Question",
      "name": "Is AI quoting software accurate enough for UK construction jobs?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "AI quoting software has reached a level of accuracy that makes it a genuinely useful starting point for UK residential construction quotes. The best tools use a UK-specific cost library, read structural drawings to extract quantities, and run a validation pass to flag scope gaps and quantity outliers. Builders should still review every AI-generated line item before sending: the AI produces a structured, priced first draft in minutes, which the builder then refines based on site-specific knowledge.",
      },
    },
    {
      "@type": "Question",
      "name": "What are the most common quoting mistakes UK builders make?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The most common quoting mistakes UK builders make are: not visiting the site before pricing, leading to missed access or demolition complications; forgetting overhead in the cost build-up; using outdated material prices; not specifying exclusions clearly; underestimating labour days on jobs with complex phasing; failing to add a risk contingency on unfamiliar work types; and sending quotes too late: many clients commit to the first builder who responds professionally.",
      },
    },
  ],
};

const FAQS = [
  {
    q: "How do UK builders price a job accurately?",
    a: "Accurate pricing for UK builders involves five cost components: labour (calculated at your true day rate, including NI, insurance, and overheads); materials (priced from current supplier quotes with a 10–15% wastage uplift); plant and hired equipment (skips, scaffolding, specialist kit); subcontractors (fixed quotes plus a 10–15% management margin); and overhead and profit (typically 10–20% net margin on top of all direct costs). The most common mistake is underestimating labour days and missing scope items entirely.",
  },
  {
    q: "What does quoting software do for a UK builder?",
    a: "Quoting software for UK builders generates a fully itemised scope of works from drawings, photos, or a plain-text description, applies UK market rates for labour and materials, checks for missing items and pricing outliers, and exports a professional PDF quote ready to send to clients. The best tools use a UK-specific cost library and can read structural drawings to extract beam sizes, floor areas, and connection details automatically.",
  },
  {
    q: "What are typical UK construction cost benchmarks for 2026?",
    a: "Indicative UK benchmarks for 2026: single-storey rear extension £1,800–£2,500/m²; double-storey extension £1,600–£2,200/m²; loft conversion £35,000–£65,000 depending on type; garage conversion £15,000–£30,000; kitchen renovation £10,000–£35,000. These are guide figures only: actual costs vary by region, specification, and site conditions. London and the South East typically run 15–25% above national averages.",
  },
  {
    q: "Is AI quoting software accurate enough for UK construction jobs?",
    a: "AI quoting software has reached a level of accuracy that makes it a genuinely useful starting point for UK residential construction quotes. The best tools use a UK-specific cost library, read structural drawings to extract quantities, and run a validation pass to flag scope gaps and quantity outliers. Builders should still review every AI-generated line item before sending: the AI produces a structured, priced first draft in minutes, which the builder then refines based on site-specific knowledge.",
  },
  {
    q: "What are the most common quoting mistakes UK builders make?",
    a: "The most common quoting mistakes UK builders make are: not visiting the site before pricing, leading to missed access or demolition complications; forgetting overhead in the cost build-up; using outdated material prices; not specifying exclusions clearly; underestimating labour days on jobs with complex phasing; failing to add a risk contingency on unfamiliar work types; and sending quotes too late: many clients commit to the first builder who responds professionally.",
  },
];

const TOC = [
  { id: "what-quoting-software-does", label: "What quoting software does" },
  { id: "how-to-price-jobs", label: "How to price construction jobs" },
  { id: "uk-cost-benchmarks", label: "UK cost benchmarks 2026" },
  { id: "ai-vs-manual", label: "AI vs manual quoting" },
  { id: "common-mistakes", label: "Common quoting mistakes" },
  { id: "related-resources", label: "Related guides & tools" },
  { id: "faq", label: "Frequently asked questions" },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border/60 pb-4 last:border-0 last:pb-0">
      <button
        className="w-full flex items-start justify-between gap-4 py-4 text-left"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="font-semibold text-base leading-snug">{q}</span>
        <ChevronDown className={`h-5 w-5 flex-shrink-0 text-muted-foreground mt-0.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="text-muted-foreground leading-relaxed pb-2">{a}</p>}
    </div>
  );
}

export default function GuideQuotingForUKBuildersPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Quoting for UK Builders: The Complete Guide (2026)"
        description="Everything UK builders need to know about quoting jobs: what quoting software does, how to price construction jobs, UK cost benchmarks, AI vs manual quoting, and common mistakes to avoid."
        canonical="/guides/quoting-for-uk-builders"
        schema={[WEBPAGE_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span className="text-muted-foreground">Guides</span>
              <ChevronRight className="h-4 w-4" />
              <span>Quoting for UK Builders</span>
            </div>
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">Complete Guide · 2026</span>
            <h1 className="text-4xl sm:text-5xl font-bold leading-tight tracking-tight mb-6">
              Quoting for UK Builders: the complete guide
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl">
              How to price jobs accurately, what quoting software actually does, UK cost benchmarks, AI vs manual quoting, and the mistakes that cost builders money every year.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" asChild>
                <Link href="/login">
                  Try The Proper Tool free
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="#what-quoting-software-does">Jump to the guide</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · No credit card required</p>
          </div>
        </section>

        {/* TOC */}
        <section className="py-8 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">In this guide</p>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5">
              {TOC.map((item, i) => (
                <li key={item.id} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground/60 w-4 text-right flex-shrink-0">{i + 1}.</span>
                  <a href={`#${item.id}`} className="text-sm text-primary hover:underline">
                    {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Section 1: What quoting software does */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="what-quoting-software-does">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">1. What quoting software does: and what it doesn't</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Builder quoting software has a straightforward job: take the information you have about a project and turn it into a priced, itemised scope of works that you can send to a client. What separates good tools from bad ones is how much of that work they do for you, and how UK-specific the output is.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              At minimum, a quoting tool should let you build a line-item breakdown without typing everything from scratch, apply stored rates to each item, and export a professional PDF. That's the baseline. The better tools go further: they read drawings and photos to extract quantities automatically, check your scope for missing items and pricing gaps, and apply a UK cost library that reflects real market rates: not generic global averages.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              What quoting software doesn't do is replace your knowledge of the job. An AI-generated scope is a structured, priced starting point. You still need to review every line, adjust quantities based on site conditions you've seen in person, and apply your own judgement about risk. The software handles the time-consuming parts: measuring, rate-lookup, formatting: so you spend your time on the decisions that require experience.
            </p>
            <h3 className="text-xl font-semibold mt-8 mb-3">What to look for in a UK builder quoting tool</h3>
            <ul className="space-y-3">
              {[
                { title: "UK-calibrated cost library", desc: "Labour rates and material costs vary significantly between the UK, Australia, and the US. Make sure the tool's rates are built for the UK market: not adapted from another country's data." },
                { title: "Reads drawings and photos", desc: "The best tools extract beam sizes, floor areas, and construction details directly from architect's drawings or structural engineer reports, removing the need to manually transfer measurements." },
                { title: "Scope completeness checks", desc: "Under-pricing usually comes from missing items, not wrong rates. A tool that actively flags scope gaps (no DPC listed, no insulation, no structural beam) is more valuable than one that simply applies rates to what you've typed." },
                { title: "Structured output", desc: "A quote broken down by trade and line item: with descriptions, quantities, and unit rates: is more defensible in disputes and gives the client confidence. A single lump-sum number is not a quote." },
                { title: "Professional PDF export", desc: "The quality of your quote document is a proxy for the quality of your work. Branded, well-structured PDFs win more jobs." },
              ].map((item) => (
                <li key={item.title} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold">{item.title}: </span>
                    <span className="text-muted-foreground">{item.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Section 2: How to price jobs */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="how-to-price-jobs">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">2. How to price construction jobs in the UK</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Every building quote, from a bathroom renovation to a full extension, is built from the same five cost components. Understanding each one is the foundation of accurate pricing.
            </p>

            <div className="space-y-8 mt-8">
              <div>
                <h3 className="text-xl font-semibold mb-3">Labour</h3>
                <p className="text-muted-foreground leading-relaxed mb-2">
                  Labour is typically the largest single cost on any building job. Your true day rate is not what you want to earn: it's your take-home plus National Insurance, public liability insurance, tool maintenance, vehicle costs, and time spent on admin and quoting. Most UK builders calculate their true hourly cost at 1.5x–2x their basic hourly rate.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  For each job, estimate labour days per trade, then apply your true day rate. Be realistic. Tight access, unpredictable existing structure, and complex phasing all add days. Always allow contingency time: especially on anything involving an existing building rather than a clean new build.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">Materials</h3>
                <p className="text-muted-foreground leading-relaxed mb-2">
                  Materials must be priced from current supplier quotes: not memory or last year's figures. Timber, insulation, brickwork, and steelwork prices all move with the market. Add a 10–15% uplift to every materials estimate to cover wastage, delivery costs, and price fluctuations between quote and order.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  For unfamiliar materials or structural elements (specialist steel connections, heritage brickwork, specific insulation specifications), get a firm quote from a supplier before you price the job.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">Plant and equipment</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Skips, scaffolding, mobile elevated work platforms, concrete mixers, and specialist kit all need to be priced individually. Include the full hire period: not the minimum. Jobs overrun, and a scaffold standing on site for an extra week adds directly to your cost if it's not covered in the quote.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">Subcontractors</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Get fixed quotes from specialist trades: electricians, plumbers, structural engineers, decorators: before including their figure in your own quote. Never guess. Add a management margin of 10–15% on top of subcontractor costs to cover coordination, quality control, and the liability you're taking on as main contractor.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">Overhead and profit</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Overhead covers the costs of running your business that are not tied to a single job: vehicle, office, accounting, insurance, marketing, tools. Divide your annual overhead by your billable days to get a daily overhead rate, then include it in every quote. Profit is separate: the return on the risk of running a business. UK builders typically target 10–20% net margin. Apply it on top of all costs, not as a substitute for overhead.
                </p>
              </div>
            </div>

            <div className="mt-10 p-5 rounded-xl bg-primary/5 border border-primary/20">
              <p className="font-semibold mb-1">Further reading</p>
              <Link href="/blog/how-to-price-building-jobs" className="text-primary hover:underline text-sm">
                How to Price Building Jobs: A Practical Guide for UK Builders →
              </Link>
            </div>
          </div>
        </section>

        {/* Section 3: UK cost benchmarks */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="uk-cost-benchmarks">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">3. UK construction cost benchmarks for 2026</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              These are indicative cost ranges for common UK residential projects in 2026. They reflect national averages across labour, materials, and plant for a standard specification. London and the South East typically run 15–25% above these figures. Complex site conditions, heritage requirements, or premium specifications will push costs higher.
            </p>

            <div className="overflow-x-auto rounded-xl border border-border mb-8">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-4 py-3 font-semibold">Project type</th>
                    <th className="text-left px-4 py-3 font-semibold">Typical range</th>
                    <th className="text-left px-4 py-3 font-semibold hidden sm:table-cell">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { type: "Single-storey rear extension", range: "£1,800–£2,500/m²", notes: "Varies widely by finish spec and structural complexity" },
                    { type: "Double-storey extension", range: "£1,600–£2,200/m²", notes: "Cost per m² lower than single-storey due to shared foundations" },
                    { type: "Loft conversion (dormer)", range: "£45,000–£65,000", notes: "Hip-to-gable or mansard types run higher" },
                    { type: "Loft conversion (roof light)", range: "£25,000–£40,000", notes: "Simplest type; no dormer structure" },
                    { type: "Garage conversion", range: "£15,000–£30,000", notes: "Depends heavily on insulation, glazing, and services" },
                    { type: "Kitchen renovation", range: "£10,000–£35,000", notes: "Broad range driven by units and appliance specification" },
                    { type: "Bathroom renovation", range: "£5,000–£15,000", notes: "Full strip-out and re-fit; sanitary ware spec is the main variable" },
                    { type: "Structural wall removal", range: "£2,500–£6,000", notes: "Includes RSJ/steel beam supply, installation, and making good" },
                  ].map((row) => (
                    <tr key={row.type} className="border-b border-border/40 last:border-0">
                      <td className="px-4 py-3 font-medium">{row.type}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.range}</td>
                      <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{row.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-sm text-muted-foreground mb-6">
              Use these benchmarks to sense-check quotes, not as a substitute for a full line-item estimate. A quote that lands significantly outside these ranges in either direction warrants careful review.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl bg-primary/5 border border-primary/20">
                <p className="font-semibold mb-1 text-sm">Related guides</p>
                <ul className="space-y-1.5">
                  <li><Link href="/blog/rear-extension-cost-uk" className="text-primary hover:underline text-sm">Rear extension costs UK →</Link></li>
                  <li><Link href="/blog/single-storey-extension-cost-uk" className="text-primary hover:underline text-sm">Single-storey extension cost UK →</Link></li>
                  <li><Link href="/blog/loft-conversion-cost-breakdown-uk" className="text-primary hover:underline text-sm">Loft conversion cost breakdown UK →</Link></li>
                  <li><Link href="/blog/garage-conversion-cost-uk" className="text-primary hover:underline text-sm">Garage conversion costs UK →</Link></li>
                </ul>
              </div>
              <div className="p-5 rounded-xl bg-primary/5 border border-primary/20">
                <p className="font-semibold mb-1 text-sm">Also see</p>
                <ul className="space-y-1.5">
                  <li><Link href="/blog/loft-conversion-cost-estimate-uk" className="text-primary hover:underline text-sm">How to price a loft conversion →</Link></li>
                  <li><Link href="/blog/kitchen-extension-cost-uk" className="text-primary hover:underline text-sm">Kitchen extension costs UK →</Link></li>
                  <li><Link href="/blog/bathroom-renovation-cost-uk" className="text-primary hover:underline text-sm">Bathroom renovation costs UK →</Link></li>
                  <li><Link href="/blog/kitchen-renovation-cost-uk" className="text-primary hover:underline text-sm">Kitchen renovation costs UK →</Link></li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: AI vs manual quoting */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="ai-vs-manual">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">4. AI quoting vs manual quoting: which is right for you?</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              For most UK residential builders doing extensions, loft conversions, and structural alterations, AI quoting is now a practical option: not a gimmick. The technology has reached a point where a well-built AI quoting tool can read an architect's drawings, identify the key structural elements, and produce a priced, itemised scope in under 10 minutes. That is a meaningful difference from a manual process that typically takes one to three hours per quote.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              <div className="bg-background rounded-xl border border-border p-6">
                <h3 className="font-bold text-lg mb-4">AI quoting</h3>
                <ul className="space-y-3">
                  {[
                    "Quote in under 10 minutes from drawings, photos, or a description",
                    "Reads structural drawings and extracts beam sizes, spans, floor areas",
                    "Built-in scope validation: catches items you might have missed",
                    "UK cost library applies real rates: no rate research needed",
                    "Consistent output format: every quote looks professional",
                    "Best for: residential extensions, loft conversions, structural alterations",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-background rounded-xl border border-border p-6">
                <h3 className="font-bold text-lg mb-4">Manual quoting</h3>
                <ul className="space-y-3">
                  {[
                    "Full control over every line item and rate from the start",
                    "No dependency on software interpreting your drawings correctly",
                    "Better suited to highly specialist or unusual work types",
                    "Works for builders with established spreadsheet templates and supplier rates",
                    "Takes 1–3 hours per quote depending on complexity",
                    "Best for: unusual job types, heritage work, civil or commercial projects",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-muted-foreground/60 flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              The practical answer for most builders is that AI quoting and manual review work together. The AI generates the structured scope with UK rates; you review every line, adjust quantities based on site knowledge, and make the final call on price. The AI handles the time-consuming parts: the builder handles the judgement.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Where AI quoting falls short is on highly unusual job types it hasn't been trained on, heritage or listed building work with bespoke specifications, and commercial or civil projects where the cost drivers are quite different to residential construction. For those jobs, a manual build-up is still the right approach.
            </p>

            <div className="p-5 rounded-xl bg-primary/5 border border-primary/20">
              <p className="font-semibold mb-1">Further reading</p>
              <div className="space-y-1.5">
                <Link href="/blog/ai-quoting-software-uk-builders" className="block text-primary hover:underline text-sm">AI Quoting Software for UK Builders: How It Works →</Link>
                <Link href="/blog/quoting-tool-for-small-builders" className="block text-primary hover:underline text-sm">Manual Quoting vs Automated Quoting: Which Is Right for You? →</Link>
                <Link href="/ai-quoting-software" className="block text-primary hover:underline text-sm">The Proper Tool: AI Quoting Software for UK Builders →</Link>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5: Common mistakes */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="common-mistakes">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">5. Common quoting mistakes UK builders make</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Most quoting errors come down to a handful of recurring patterns. Recognising them is the first step to avoiding them.
            </p>

            <div className="space-y-6">
              {[
                {
                  title: "Not visiting the site before pricing",
                  body: "Drawings and photos don't tell you about a 900mm-wide side access that makes skip delivery impossible, or a party wall in worse condition than it looks. A site visit costs an hour. A missed access problem costs days. Always visit before you price anything complex.",
                },
                {
                  title: "Missing scope items entirely",
                  body: "The most expensive quoting mistakes come from leaving trade items out of the scope altogether: no DPC, no soil vent pipe re-routing, no structural packing to beam bearing plates. These aren't pricing errors; they're scope errors. A validation check (manual or software-assisted) that asks 'what would typically follow this element?' catches them before you send.",
                },
                {
                  title: "Using outdated material prices",
                  body: "Material prices move with the market. Timber, insulation, and structural steel in particular have seen significant volatility since 2021. A quote built on prices from 18 months ago can be 10–20% light on materials alone. Price materials from current supplier quotes on every job, and add a 10–15% wastage and volatility buffer.",
                },
                {
                  title: "Forgetting overhead in the cost build-up",
                  body: "Many builders price jobs for their direct costs (labour, materials, plant) and then add a profit margin: but forget that overhead is not covered by profit. Overhead is the cost of running your business. If you don't include it in your quotes, you are effectively funding your overhead from profit, which erodes your margin on every job.",
                },
                {
                  title: "Not specifying exclusions clearly",
                  body: "A quote without clear exclusions creates disputes when the job starts. 'Decoration not included', 'electrical first-fix only, second-fix by others', 'assumes no asbestos present': these aren't pedantic caveats. They're the boundaries of what you've priced. Clients who read quotes carefully will ask about exclusions before they sign. Clients who don't will raise them as variations on site.",
                },
                {
                  title: "Responding too slowly",
                  body: "Many clients commission the first builder who comes back to them with a professional, complete quote. If your quoting process takes a week, you are losing work to competitors who quote in a day. Speed of response is a competitive advantage: especially on smaller residential jobs where clients are often deciding between two or three builders simultaneously.",
                },
                {
                  title: "Pricing labour on best-case productivity",
                  body: "Labour estimates based on how fast the job would go if everything went perfectly are consistently wrong. Allow for tool setup, site protection, material handling, and the inevitable problem that emerges on day two of every job. A 10–15% contingency on labour days is not excessive; it's professional.",
                },
              ].map((item, i) => (
                <div key={item.title} className="flex items-start gap-4 p-5 rounded-xl border border-border bg-background">
                  <span className="text-2xl font-bold text-primary/30 flex-shrink-0 w-8 text-right">{i + 1}</span>
                  <div>
                    <h3 className="font-semibold text-base mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-5 rounded-xl bg-primary/5 border border-primary/20">
              <p className="font-semibold mb-1">Further reading</p>
              <div className="space-y-1.5">
                <Link href="/blog/what-to-include-in-builders-quote" className="block text-primary hover:underline text-sm">What to include in a builder's quote →</Link>
                <Link href="/blog/extension-quote-template-uk" className="block text-primary hover:underline text-sm">Extension quote template for UK builders →</Link>
              </div>
            </div>
          </div>
        </section>

        {/* Related resources cluster */}
        <section className="py-12 border-t border-border/40 bg-muted/20" id="related-resources">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related guides and tools</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { href: "/blog/how-to-price-building-jobs", label: "How to price building jobs (UK guide)" },
                { href: "/blog/builder-quote-app-uk", label: "Best builder quote app for UK tradespeople" },
                { href: "/blog/quoting-tool-for-small-builders", label: "Manual vs automated quoting for builders" },
                { href: "/blog/ai-quoting-software-uk-builders", label: "AI quoting software for UK builders" },
                { href: "/blog/free-quoting-software-builders-uk", label: "Free quoting software for UK builders" },
                { href: "/best/quoting-software-for-uk-builders", label: "Best quoting software for UK builders: comparison" },
                { href: "/ai-quoting-software", label: "AI quoting app: how it works" },
                { href: "/for-small-builders", label: "Quoting software for small builders" },
                { href: "/estimating-software-for-builders", label: "Estimating software for builders" },
                { href: "/guides/running-a-building-business-uk/", label: "Running a building business in the UK" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="faq">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-0">
              {FAQS.map((faq) => (
                <FaqItem key={faq.q} q={faq.q} a={faq.a} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Quote faster, win more jobs</h2>
            <p className="text-lg text-muted-foreground mb-8">
              The Proper Tool generates a fully itemised, UK-priced quote from your drawings, photos, or a plain description in under 10 minutes. 14-day free trial, no credit card required.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">No credit card required · Cancel any time · Plans from £29/mo</p>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
